import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Frame from "../widgets/Frame";
import axios from "axios";
import { useSelector } from "react-redux";
import { isPending2FA, userToken } from "../../features/user";
import { API_URL, API_OK } from "../../Constants";
import { getLang } from "../../Helper";
import { passkeyDisplayName, passkeyKindLabel, passkeysSupported } from "../../Passkey";
import { useSubmitGuard } from "../../hooks/useSubmitGuard";
import { useCaptcha } from "../../captcha";
import { useEnterSubmit } from '../../hooks/useEnterSubmit';

export function AuthMethodSelection() {
  const [selectedVerification, setSelectedVerification] = useState({
    method: "",
    uuid: "",
  });
  const [openPanel, setOpenPanel] = useState({
    phoneCall: false,
    sms: false,
    email: false,
    app: false,
    passkey: false,
    backupCode: false,
  });
  const [data, setData] = useState([]);
  // Each passkey is its own row: unlike a phone number, what tells two of them
  // apart is the name and the model, which the server resolves for us.
  const [passkeys, setPasskeys] = useState([]);
  const [app, setApp] = useState({
    uuid: '',
    method: "app",
    dateAdded: "22.06.2025",
  });
  const [backup] = useState({
    method: "backup",
  });
  /**
   * כמה קודי גיבוי שלא נוצלו יש לחשבון.
   *
   * מגיע מ-`getAll2faMethods` ולא מנוחש: קודי גיבוי נוצרים עם השיטה הראשונה, ומי
   * שניצל את כולם או שאין לו כאלה אינו יכול להיכנס בהם. מקטע שמוצג בלי זה הוא מקטע
   * שנפתח, מסמנים בו, ולוחצים "המשך" - ונדחים.
   */
  const [recoveryCodesCount, setRecoveryCodesCount] = useState(0);

  const navigate = useNavigate();
  const { t } = useTranslation();
  // Continuing sends a code, so the click must not be repeatable while it is in flight.
  const submit = useSubmitGuard();
  /**
   * הקאפצ'ה של שליחת הקוד. אותה פעולה כמו במסך הקוד עצמו, כי זהו אותו שלב אימות.
   */
  const captcha = useCaptcha('twoFactor');
  const isUserPending2FA = useSelector(isPending2FA);
  let config = { headers: { token: userToken(), lang: getLang() } };

  const normalizeMethods = (methods) => {
    if (!Array.isArray(methods)) {
      return [];
    }

    return methods
      .map((method) => {
        if (!method) {
          return null;
        }

        const uuid = method.uuid;
        const methodName = (method.method || "").toLowerCase();

        return {
          uuid: uuid || "",
          method: methodName,
          createdAt: method.created_at || method.dateAdded || "",
          phoneNumber: method.phone_number || method.phoneNumber || undefined,
          emailAddress: method.email || method.email_address || method.emailAddress || undefined,
          // Name, model and sync state, for the passkey rows below.
          passkey: method.passkey || undefined,
          isDefault: Boolean(method.isDefault),
        };
      })
      .filter(Boolean);
  };

  const hydrateFromMethods = (methods, recoveryCodes) => {
    const normalizedMethods = normalizeMethods(methods);
    setRecoveryCodesCount(Number(recoveryCodes) || 0);

    if (normalizedMethods.length === 0) {
      return false;
    }

    const filteredMethods = normalizedMethods
      .filter((m) => m.method === "sms" || m.method === "voice" || m.method === "email")
      .map((m) => ({
        uuid: m.uuid,
        method: m.method,
        dateAdded: m.createdAt.slice(0, 10),
        phoneNumber: m.phoneNumber,
        emailAddress: m.emailAddress,
      }));

    setData(filteredMethods);

    /*
     * Passkeys are listed only when this browser can actually use one.
     *
     * A row that leads to "your browser does not support this" is a dead end
     * offered at the exact moment the user is trying to get in; the other
     * methods are still there, and they are what this screen should show.
     */
    setPasskeys(
      passkeysSupported()
        ? normalizedMethods
            .filter((m) => m.method === "passkey")
            .map((m) => ({
              uuid: m.uuid,
              method: m.method,
              dateAdded: m.createdAt.slice(0, 10),
              passkey: m.passkey,
            }))
        : []
    );

    const appMethod = normalizedMethods.find((m) => m.method.toLowerCase() === "app");

    if (appMethod) {
      setApp({
        uuid: appMethod.uuid,
        method: appMethod.method,
        dateAdded: appMethod.createdAt.slice(0, 10),
      });
    } else {
      setApp(null);
    }

    return normalizedMethods;
  };

  const persistMethods = (methods, recoveryCodes) => {
    try {
      sessionStorage.setItem("2fa_methods", JSON.stringify({ methods, recoveryCodesCount: recoveryCodes }));
    } catch (error) {
      console.error("Failed to cache 2FA methods in sessionStorage:", error);
    }
  };

  const getMethods = async () => {
    try {
      const storedMethodsData = sessionStorage.getItem("2fa_methods");
      if (storedMethodsData) {
        const parsedData = JSON.parse(storedMethodsData);
        /*
         * מטמון שנכתב לפני שספירת קודי הגיבוי נשמרה בו אינו נקרא.
         *
         * "אין שדה" אינו "אין קודים": קריאה שלו כאפס הייתה מסתירה את המקטע ממי שכן
         * יש לו קודים, ורק כי הלשונית נטענה פעם קודם. עדיף לפנות לשרת.
         */
        if (typeof parsedData.recoveryCodesCount === "number") {
          const hydrated = hydrateFromMethods(parsedData.methods, parsedData.recoveryCodesCount);
          if (hydrated) {
            return;
          }
        }
      }
    } catch (error) {
      console.error("Error parsing stored 2FA methods:", error);
    }

    try {
      const response = await axios.get(API_URL + "getAll2faMethods", config);
      if (response?.data?.responseStatus && response.data.responseStatus !== API_OK) {
        console.error(
          "Unexpected response status when fetching 2FA methods:",
          response.data.responseStatus
        );
        setData([]);
        return;
      }

      const responseMethods =
        response?.data?.methods ?? response?.data?.data?.methods ?? null;

      const responseRecoveryCodes = response?.data?.recoveryCodesCount
        ?? response?.data?.data?.recoveryCodesCount
        ?? 0;

      const hydrated = hydrateFromMethods(responseMethods, responseRecoveryCodes);
      if (hydrated) {
        persistMethods(hydrated, responseRecoveryCodes);
        return;
      }

      setData([]);
    } catch (error) {
      console.error("Error fetching 2FA methods:", error);
      setData([]);
    }
  };

  useEffect(() => {
    if (!isUserPending2FA) {
      navigate("/login");
    }
    getMethods();
    // eslint-disable-next-line
  }, []);

  /**
   * מה שיש **לחשבון הזה**, ולא מה שהמערכת יודעת לעשות.
   *
   * המסך הציג את כל ששת המקטעים תמיד: שיחה, מסרון, דואר, אפליקציה, מפתח גישה וקודי
   * גיבוי. מי שיש לו מסרון בלבד ראה חמישה מקטעים שנפתחים לטבלה ריקה - ורשימה שרובה
   * מוביל לשום מקום היא רשימה שאי אפשר לבחור ממנה. מפתחות הגישה כבר היו מגודרים
   * (`passkeys.length > 0`), וזו אותה גדר לחמישה האחרים.
   *
   * קודי הגיבוי נגדרים בספירה מהשרת ולא בהנחה: הם נוצרים עם השיטה הראשונה, אבל כל
   * אחד מהם נשרף בשימוש - ומי שניצל את כולם אינו יכול להיכנס בהם.
   */
  const hasMethod = (method) => data.some((row) => row.method === method);
  const hasVoice = hasMethod("voice");
  const hasSms = hasMethod("sms");
  const hasEmail = hasMethod("email");
  const hasApp = Boolean(app);
  const hasBackup = recoveryCodesCount > 0;
  const hasAnyMethod = hasVoice || hasSms || hasEmail || hasApp || passkeys.length > 0 || hasBackup;

  const handleSelectVerification = (method, uuid) => {
    setSelectedVerification({
      method,
      uuid,
    });
  };

  const handlePanelToggle = (panel) => {
    setOpenPanel((prev) => ({
      ...prev,
      [panel]: !prev[panel],
    }));
  };

  const handleContinue = async () => {
    const methodUuid = selectedVerification.uuid;

    if (
      selectedVerification.method === "voice" ||
      selectedVerification.method === "sms" ||
      selectedVerification.method === "email"
    ) {
      if (!methodUuid) {
        console.error("No valid method uuid available for 2FA resend request");
        return;
      }

      // הטוקן מצורף כאן, כדי שהפעולה שהוא נוצר עבורה תיקבע במקום אחד.
      const payload = await captcha.attach({ methodUuid: methodUuid });
      // אין טוקן - אין מה לשלוח. ההסבר כבר מצויר על המסך.
      if (!payload) {
        return;
      }

      const sent = await submit.run(() =>
        axios.post(API_URL + "resend2faCode", payload, config)
      );
      // A blocked second click returns nothing; the first send is still running.
      if (!sent) {
        return;
      }
    }

    if (
      methodUuid ||
      selectedVerification.method === "backup"
    ) {
      const methodUuidForState =
        selectedVerification.method === "backup"
          ? "backup"
          : methodUuid;

      const methodUuidQuery =
        methodUuid
          ? methodUuid
          : "";

      navigate(
        `/login-verify?method=${selectedVerification.method}${
          methodUuidQuery ? `&methodUuid=${methodUuidQuery}` : ""
        }`,
        {
          state: {
            method: selectedVerification.method,
            methodUuid: methodUuidForState,
          },
        }
      );
    }
  };

  const handleBack = () => {
    navigate(-1);
  };  

  /**
   * Enter ממשיך לשלב הבא. ראה `useEnterSubmit` - הפעולה הראשית של המסך אינה
   * כפתור שליחה אלא `div`, ולכן הדפדפן אינו שולח כאן דבר מעצמו.
   */
  useEnterSubmit(handleContinue, !submit.busy);

  return (
    <Frame>
      <div className="main main-login-verify">
        <div className="main-top">
          <h1>{t("Two-factor authentication")}</h1>
        </div>
        <div className="main-body">
          <div className="right-main full-width">
            <div className="right-main-inner">
              <h2>
                {t(
                  "Please select the method you would like to receive the verification code"
                )}
              </h2>
              {hasVoice && (
              <div className="panel">
                <div
                  className={`panel-header ${
                    openPanel.phoneCall ? "panel-header-active" : ""
                  }`}
                  onClick={() => handlePanelToggle("phoneCall")}
                >
                  <div className="panel-title">
                    <img
                      src="/images/phone-call-icon.png"
                      className="panel-icon"
                      alt="Phone Call"
                    />
                    <div>{t("Receive code via phone call")}</div>
                  </div>
                  <div
                    className={`chevron-icon  ${
                      openPanel.phoneCall ? "chevron-icon-active" : ""
                    }`}
                  >
                    <img src="/images/open-dropdown-icon.png" alt="Chevron" />
                  </div>
                </div>

                {openPanel.phoneCall && (
                  <div className="panel-content">
                    <table className="full-width-table">
                      <thead>
                        <tr className="table-header">
                          <th></th>
                          <th>{t("Phone Number for Verification")}</th>
                          <th>{t("Date Added")}</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {data
                          .filter((row) => row.method === "voice")
                          .map((row) => (
                            <tr key={row.uuid} className="table-row">
                              <td>
                                <input
                                  className="radio-button"
                                  type="radio"
                                  name="methodSelection"
                                  value={row.uuid}
                                  checked={selectedVerification.uuid === row.uuid}
                                  onChange={() =>
                                    handleSelectVerification(row.method, row.uuid)
                                  }
                                />
                              </td>
                              <td><span className="ltr-span">{row.phoneNumber}</span></td>
                              <td>{row.dateAdded}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* Receive code via SMS section */}
              {hasSms && (
              <div className="panel">
                <div
                  className={`panel-header ${
                    openPanel.sms ? "panel-header-active" : ""
                  }`}
                  onClick={() => handlePanelToggle("sms")}
                >
                  <div className="panel-title">
                    <img
                      src="/images/phone-icon.png"
                      className="panel-icon"
                      alt="SMS"
                    />
                    <div>{t("Receive code via SMS")}</div>
                  </div>
                  <div
                    className={`chevron-icon  ${
                      openPanel.sms ? "chevron-icon-active" : ""
                    }`}
                  >
                    {" "}
                    <img src="/images/open-dropdown-icon.png" alt="Chevron" />
                  </div>
                </div>

                {openPanel.sms && (
                  <div className="panel-content">
                    <table className="full-width-table">
                      <thead>
                        <tr className="table-header">
                          <th></th>
                          <th>{t("Phone Number for Verification")}</th>
                          <th>{t("Date Added")}</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {data
                          .filter((row) => row.method === "sms")
                          .map((row) => (
                            <tr key={row.uuid} className="table-row">
                              <td>
                                <input
                                  className="radio-button"
                                  type="radio"
                                  name="methodSelection"
                                  value={row.uuid}
                                  checked={selectedVerification.uuid === row.uuid}
                                  onChange={() =>
                                    handleSelectVerification(row.method, row.uuid)
                                  }
                                />
                              </td>
                              <td><span className="ltr-span">{row.phoneNumber}</span></td>
                              <td>{row.dateAdded}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* Use Email section */}
              {hasEmail && (
              <div className="panel">
                <div
                  className={`panel-header ${
                    openPanel.email ? "panel-header-active" : ""
                  }`}
                  onClick={() => handlePanelToggle("email")}
                >
                  <div className="panel-title">
                    <img
                      src="/images/email-icon.png"
                      className="panel-icon"
                      alt="Email"
                    />
                    <div>{t("Receive code via email")}</div>
                  </div>
                  <div
                    className={`chevron-icon  ${
                      openPanel.email ? "chevron-icon-active" : ""
                    }`}
                  >
                    {" "}
                    <img src="/images/open-dropdown-icon.png" alt="Chevron" />
                  </div>
                </div>
                {openPanel.email && (
                  <div className="panel-content">
                    <table className="full-width-table">
                      <thead>
                        <tr className="table-header">
                          <th></th>
                          <th>{t("Email Address for Verification")}</th>
                          <th>{t("Date Added")}</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {data
                          .filter((row) => row.method === "email")
                          .map((row) => (
                            <tr key={row.uuid} className="table-row">
                              <td>
                                <input
                                  className="radio-button"
                                  type="radio"
                                  name="methodSelection"
                                  value={row.uuid}
                                  checked={selectedVerification.uuid === row.uuid}
                                  onChange={() =>
                                    handleSelectVerification(row.method, row.uuid)
                                  }
                                />
                              </td>
                              <td><span className="ltr-span">{row.emailAddress}</span></td>
                              <td>{row.dateAdded}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/* Use App section */}
              {hasApp && (
              <div className="panel">
                <div
                  className={`panel-header ${
                    openPanel.app ? "panel-header-active" : ""
                  }`}
                  onClick={() => handlePanelToggle("app")}
                >
                  <div className="panel-title">
                    <img
                      src="/images/app-icon.png"
                      className="panel-icon"
                      alt="App"
                    />
                    <div>{t("Receive code via app")}</div>
                  </div>
                  <div
                    className={`chevron-icon  ${
                      openPanel.app ? "chevron-icon-active" : ""
                    }`}
                  >
                    {" "}
                    <img src="/images/open-dropdown-icon.png" alt="Chevron" />
                  </div>
                </div>
                {openPanel.app && (
                  <div className="panel-content">
                    <table className="full-width-table">
                      <thead>
                        <tr className="table-header">
                          <th>
                            {app && (
                              <input
                                className="radio-button"
                                type="radio"
                                name="methodSelection"
                                value={app.uuid}
                                checked={selectedVerification.uuid === app.uuid}
                                onChange={() =>
                                  handleSelectVerification("app", app.uuid)
                                }
                              />
                            )}
                          </th>
                          <th>{t("App Verification Code")}</th>
                          <th>{t("Date Added")}</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {app && (
                          <tr key={app.uuid} className="table-row">
                            <td></td>
                            <td>{t("Authenticator App")}</td>
                            <td>{app.dateAdded}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/*
                Passkeys.

                A table like the others, but the columns are what actually
                separates two keys: the name the user gave it, what kind of key
                it is, and when it was added. "Passkey" three times over is not
                a choice, and this screen exists to make one.
              */}
              {passkeys.length > 0 && (
                <div className="panel">
                  <div
                    className={`panel-header ${
                      openPanel.passkey ? "panel-header-active" : ""
                    }`}
                    onClick={() => handlePanelToggle("passkey")}
                  >
                    <div className="panel-title">
                      <img
                        src="/images/passkey-icon.svg"
                        className="panel-icon"
                        alt=""
                      />
                      <div>{t("Approve with a passkey")}</div>
                    </div>
                    <div
                      className={`chevron-icon  ${
                        openPanel.passkey ? "chevron-icon-active" : ""
                      }`}
                    >
                      <img src="/images/open-dropdown-icon.png" alt="Chevron" />
                    </div>
                  </div>

                  {openPanel.passkey && (
                    <div className="panel-content">
                      <table className="full-width-table">
                        <thead>
                          <tr className="table-header">
                            <th></th>
                            <th>{t("Passkey")}</th>
                            <th>{t("Maker")}</th>
                            <th>{t("Type")}</th>
                            <th>{t("Date Added")}</th>
                          </tr>
                        </thead>
                        <tbody className="table-body">
                          {passkeys.map((row) => (
                            <tr key={row.uuid} className="table-row">
                              <td>
                                <input
                                  className="radio-button"
                                  type="radio"
                                  name="methodSelection"
                                  value={row.uuid}
                                  checked={selectedVerification.uuid === row.uuid}
                                  onChange={() =>
                                    handleSelectVerification(row.method, row.uuid)
                                  }
                                />
                              </td>
                              <td>
                                {passkeyDisplayName(row.passkey, t("Passkey"))}
                              </td>
                              <td>
                                {row.passkey?.vendor || t("Not identified")}
                              </td>
                              <td>{t(passkeyKindLabel(row.passkey))}</td>
                              <td>{row.dateAdded}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Use Backup Code section */}
              {hasBackup && (
              <div className="panel">
                <div
                  className={`panel-header ${
                    openPanel.backup ? "panel-header-active" : ""
                  }`}
                  onClick={() => handlePanelToggle("backup")}
                >
                  <div className="panel-title">
                    <img
                      src="/images/backup-code-icon.png"
                      className="panel-icon"
                      alt="Backup Code"
                    />
                    <div>{t("Use backup code")}</div>
                  </div>
                  <div
                    className={`chevron-icon  ${
                      openPanel.backup ? "chevron-icon-active" : ""
                    }`}
                  >
                    <img src="/images/open-dropdown-icon.png" alt="Chevron" />
                  </div>
                </div>
                {openPanel.backup && (
                  <div className="panel-content">
                    <table className="full-width-table">
                      <thead>
                        <tr className="table-header">
                          <th>
                            <input
                              className="radio-button"
                              type="radio"
                              name="methodSelection"
                              value="backup"
                              checked={selectedVerification.method === "backup"}
                              onChange={() =>
                                handleSelectVerification("backup")
                              }
                            />
                          </th>
                          <th>{t("Use A Saved Backup Code")}</th>
                          {/* <th>{t("Date Added")}</th> */}
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        <tr key="backup" className="table-row">
                          <td></td>
                          <td>{backup.phoneNumber}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              )}

              {/*
                חשבון שאין לו שיטה זמינה בדפדפן הזה.

                אומר את זה במפורש, ולא משאיר רשימה ריקה: מסך בחירה בלי מה לבחור הוא
                מסך שנראה שבור. זה קורה למשל כשהשיטה היחידה היא מפתח גישה והדפדפן
                אינו תומך בו - ואז "חזרה" הוא באמת מה שיש לעשות.
              */}
              {!hasAnyMethod && (
                <h2 className="mt20">{t("No verification method is available for your account right now.")}</h2>
              )}
            </div>
          </div>
          {/*
            עמודת הפעולות - `main-actions` ולא `left-main`.

            `left-main` היא עמודת התמונה, והיא `display: none` מתחת ל-850px: הכפתורים
            שיושבו בה נעלמו בנייד לגמרי, כלומר מסך בחירה שאין ממנו "המשך". והפריסה
            עברה מ-`style` inline ל-CSS מאותה סיבה - סגנון inline אינו יכול להשתנות
            ב-media, ולכן "חזרה" נצמד לראש המסך גם בנייד.
          */}
          <div className="main-actions">
            {/* Back button */}
            <div
              className="button button-space-between button-transparent-red"
              onClick={handleBack}
            >
              <div className="mr20">{t("Back")}</div>
            </div>
            {captcha.node}
            {/* Continue button */}
            <div
              {...submit.buttonProps({
                base: `button button-space-between button-teal ${
                  selectedVerification.uuid ||
                  selectedVerification.method === "backup"
                    ? ""
                    : "disabled"
                }`,
              })}
              onClick={handleContinue}
            >
              <div className="mr20">
                {submit.busy ? t("Sending...") : t("Continue")}
              </div>
              {submit.busy ? (
                <span className="button-spinner" />
              ) : (
                <img
                  src="/images/less-than-white.png"
                  className="button-icon less-than-icon"
                  alt=""
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Frame>
  );
}
